import { RangeDateInput } from '@core/components/Inputs';
import { SelectInput } from '@core/components/Select';
import { useReduxLoading } from '@core/hooks';
import { Card, CardContent, Grid, makeStyles } from '@material-ui/core';
import SelectConta from 'app/components/Select/SelectConta';
import clsx from 'clsx';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ConciliacaoContext } from '../ConciliacaoContext';

const classStyle = makeStyles(() => ({
  content: {
    margin: '30px',
  },
  barra: {
    background: '#e2e0e0',
    width: '100%',
    height: '30px',
    display: 'flex',
    textAlign: 'center',
    margin: '10px 13px',
  },
  barraTexto: { alignSelf: 'center', fontSize: '19px', fontWeight: 'bold', fontStretch: 'normal' },
}));

const Filtros = ({ formik, leituraArquivo, buscarLancamentos }) => {
  const [loading] = useState(false);
  const [, setContaBancaria] = useContext(ConciliacaoContext).contaBancaria;
  const [, setFiltro] = useContext(ConciliacaoContext).filtro;
  //const [leituraArquivo] = useContext(ConciliacaoContext).leituraArquivo;
  const filtro = useRef({});

  useReduxLoading(loading);

  const _errors = {
    conta: (formik?.submitCount > 0 || formik?.touched?.conta) && formik?.errors?.conta,
    periodo:
      (formik?.submitCount > 0 || formik?.touched?.dataInicio) &&
      formik?.errors?.dataInicio &&
      (formik?.submitCount > 0 || formik?.touched?.dataFim) &&
      formik?.errors?.dataFim,
  };

  const valuesConta = formik.values?.conta;
  useEffect(() => {
    if (!leituraArquivo) {
      filtro.current = {
        ...filtro.current,
        contaId: valuesConta?.id,
        contaBancariaId: valuesConta?.contaBancaria?.id,
        bancoId: valuesConta?.contaBancaria?.banco?.id,
        contaBancariaNomeReferencia: valuesConta?.contaBancaria?.banco?.nome,
        contaBancariaAgencia: valuesConta?.contaBancaria?.agencia,
        contaBancariaConta: valuesConta?.contaBancaria?.numeroConta,
      };

      let contaTipo;

      if (valuesConta?.id !== undefined && valuesConta?.id !== null) {
        if (valuesConta?.tipo === 'CONTA_CORRENTE') {
          contaTipo = 'Conta corrente';
        } else if (valuesConta?.tipo === 'CONTA_POUPANCA') {
          contaTipo = 'Conta poupança';
        } else {
          contaTipo = 'Conta investimento';
        }

        setContaBancaria({
          contaDescricao: valuesConta?.descricao,
          contaId: valuesConta?.id,
          contaBancariaNomeReferencia: valuesConta?.contaBancaria?.banco?.nome,
          contaBancariaAgencia: valuesConta?.contaBancaria?.agencia + '-' + valuesConta?.contaBancaria?.agenciaDigito,
          contaBancariaConta: valuesConta?.contaBancaria?.numeroConta + '-' + valuesConta?.contaBancaria?.contaDigito,
          contaTipo: contaTipo,
        });
      } else {
        setContaBancaria(undefined);
      }
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesConta, setContaBancaria]);

  const valuesDataInicio = formik.values?.dataInicio;
  const valuesDataFim = formik.values?.dataFim;
  useEffect(() => {
    if (!leituraArquivo) {
      filtro.current = {
        ...filtro.current,
        dataInicio: valuesDataInicio === undefined ? valuesDataInicio : valuesDataInicio.format('YYYY-MM-DD'),
        dataFim: valuesDataFim === undefined ? valuesDataFim : valuesDataFim.format('YYYY-MM-DD'),
      };
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesDataInicio, valuesDataFim]);

  const valuesTipo = formik.values?.tipo;
  useEffect(() => {
    if (!leituraArquivo) {
      filtro.current = {
        ...filtro.current,
        tipo: valuesTipo,
      };
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesTipo]);

  const valuesSituacao = formik.values?.situacao;
  useEffect(() => {
    if (!leituraArquivo) {
      filtro.current = {
        ...filtro.current,
        situacao: valuesSituacao,
      };
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSituacao]);

  useEffect(() => {
    const buscar = async () => {
      if (
        filtro.current.contaId !== undefined &&
        filtro.current.dataInicio !== undefined &&
        filtro.current.dataFim !== undefined &&
        filtro.current.tipo !== undefined &&
        filtro.current.situacao !== undefined &&
        !leituraArquivo
      ) {
        setFiltro(filtro.current);
        await buscarLancamentos(filtro.current);
      }
    };

    buscar().catch(console.error);
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  return (
    <Grid container spacing={3}>
      <Grid item lg={3} xl={3}>
        <SelectConta
          id="conta"
          name="conta"
          label="Conta bancária"
          onChange={(event) => {
            formik.handleChange(event);
          }}
          tiposConta={['CONTA_CORRENTE', 'CONTA_POUPANCA', 'CONTA_INVESTIMENTO']}
          onBlur={formik.handleBlur}
          value={formik.values?.conta}
          error={_errors.conta}
          touched={!!_errors.conta}
          required
        />
      </Grid>
      <Grid item lg={3} xl={3}>
        <RangeDateInput
          id="periodoExtrato"
          inputVariant="outlined"
          label="Período"
          value={[formik.values?.dataInicio, formik.values?.dataFim]}
          onBlur={formik.handleBlur}
          onChange={(dataInicio, dataFim) => {
            formik.setFieldValue('dataInicio', dataInicio);
            formik.setFieldValue('dataFim', dataFim);
          }}
          error={_errors.periodo}
          touched={!!_errors.periodo}
          format="DD/MM/YYYY"
          openTo="date"
          views={['date', 'month', 'year']}
          cancelLabel={false}
          okLabel={false}
          autoOk
          fullWidth
          required
        />
      </Grid>
      <Grid item lg={3} xl={3}>
        <SelectInput
          label="Tipo"
          id="tipo"
          onChange={formik.handleChange}
          value={formik.values?.tipo}
          disableClearable
          options={[
            { title: 'Todos', id: 'TODOS' },
            { title: 'Crédito', id: 'CREDIT' },
            { title: 'Débito', id: 'DEBIT' },
          ]}
        />
      </Grid>
      <Grid item lg={3} xl={3}>
        <SelectInput
          label="Situação"
          id="situacao"
          disableClearable
          onChange={formik.handleChange}
          value={formik.values?.situacao}
          options={[
            { title: 'Não conciliados', id: 'NAO_CONCILIADO' },
            { title: 'Conciliados', id: 'CONCILIADO' },
          ]}
        />
      </Grid>
    </Grid>
  );
};

const Barra = () => {
  const classes = classStyle();
  return (
    <>
      <Grid item lg={12} xl={12} className={classes.barra}>
        <Grid item lg={6} xl={6} className={clsx(classes.barraTexto, 'stepExtratoBancario')}>
          EXTRATO BANCÁRIO
        </Grid>
        <Grid item lg={6} xl={6} className={clsx(classes.barraTexto, 'stepLancamentosSistema')}>
          LANÇAMENTOS NO SISTEMA
        </Grid>
      </Grid>
    </>
  );
};

const Lancamentos = ({ children }) => {
  return (
    <Grid item lg={12} xl={12}>
      {children}
    </Grid>
  );
};

const ContainerLancamentos = ({ children, formikFiltro, buscarLancamentos }) => {
  const classes = classStyle();
  return (
    <Card>
      <CardContent className={classes.content}>
        <Grid container spacing={3}>
          <Grid item className={'stepFiltros'} lg={12} xl={12}>
            <Filtros formik={formikFiltro} buscarLancamentos={buscarLancamentos} />
          </Grid>
          <Barra />
          <Lancamentos>{children}</Lancamentos>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ContainerLancamentos;
